import styled, { css } from 'styled-components';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import { useEffect, useMemo, useState } from 'react';
import { sumBy } from 'lodash-es';
import AdaptButton from 'components/base/Button';
import { Item, IItem } from '../Item';
import { SplitBillService } from 'services/split-bill-service';
import { usePlacingPayment } from 'contexts/placingPayment';
import apiUrlProviderEvent, { GET_EVENTS } from 'constants/event-api-endpoint';
import AdaptSplitAmountField from 'components/AdaptSplitAmountField/adapt-split-amount-field.component';
import { formatPrice } from 'utils/number';
import { FORM_ERROR } from 'final-form';
import routeUrlProvider, { ERROR_FULL_ACCESS, NOTFOUND } from 'constants/route-paths';
import { useHistory } from 'react-router-dom';
import { LoadingIcon } from 'components/FullPageSpinner';
import { SESSION } from 'constants/enum/session';
import { SSE_TYPE } from 'constants/enum/see-type';

const Confirm = styled(AdaptButton)`
  && {
    font-weight: 700;
    margin-top: 16px;
    height: 56px;
  }
`;

const Center = styled.div`
  position: fixed; /* or absolute */
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Card = styled.div`
  width: 600px;
  text-align: center;
  background: ${({ theme: { color } }) => color.white};
  border-radius: 8px;
  margin-bottom: 16px;
  padding: 48px;
  ${({ theme: { mui } }) => css`
    ${mui.breakpoints.down('sm')} {
      display: flex;
      flex-direction: column;
      padding: 16px;
      width: 500px;
    }
  `}

  ${({ theme: { mui } }) => css`
    ${mui.breakpoints.down('xs')} {
      display: flex;
      flex-direction: column;
      padding: 16px;
      max-width: 310px;
    }
  `}
`;

const InputErrorMessage = styled(Box)`
  color: ${({ theme: { color } }) => color.red};
  font-weight: bold;
  font-size: 16px;
  line-height: 16px;
  text-align: center;
  margin: 20px 0px;
`;

const ItemList = () => {
  const token = sessionStorage?.getItem(SESSION.TOKEN) || '';
  const { t } = useTranslation();
  const history = useHistory();

  const { paymentInfo } = usePlacingPayment();

  const [summaryTotal, setSummaryTotal] = useState<Number>(0);
  const [select, setSelect] = useState<IItem[]>([]);
  const [items, setItems] = useState<IItem[]>([]);

  const splitBillService = useMemo(() => {
    return new SplitBillService();
  }, []);

  useEffect(() => {
    fatchData();
    handleEventSource();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fatchData = () => {
    sessionStorage?.setItem(SESSION.HEAD_SPLIT_TOKEN, token);
    splitBillService
      .getItems(token)
      .then((res) => setItems(res.data))
      .catch((error) => {
        if (error.statusCode && error.statusCode === 403) {
          history.push(routeUrlProvider.getForRoute(ERROR_FULL_ACCESS));
        } else {
          history.push(routeUrlProvider.getForRoute(NOTFOUND));
        }
      });
  };

  const handleEventSource = () => {
    const url = apiUrlProviderEvent.get(GET_EVENTS, { tid: paymentInfo?.pid });
    const eventSource = new EventSource(`${url}?publish=all`);

    const handleMessage = (event: MessageEvent) => {
      const parsedData = JSON.parse(event.data);

      if (parsedData?.message?.type === SSE_TYPE.STATUS) {
        fatchData();
      }
    };
    eventSource.onmessage = handleMessage;

    return eventSource;
  };

  const total = useMemo(() => {
    return sumBy(select, 'total_price');
  }, [select]);

  const confirm = () => {
    const selected = [];
    for (const selectData of select) {
      selected.push(selectData.id);
    }

    if (selected.length === 0) {
      return { [FORM_ERROR]: t('splitBillValidationByItem') };
    }

    return splitBillService
      .splitByItem(token, {
        menuId: selected,
      })
      .then((res) => window.open(res.token, '_self'))
      .then(() => {
        setSummaryTotal(total);
        setSelect([]);
        fatchData();
      });
  };

  return (
    <Form
      onSubmit={confirm}
      render={({ handleSubmit, submitting, submitError }) => (
        <form onSubmit={handleSubmit}>
          <Center>
            <Card>
              <Field
                bgColor="white_600"
                name="splitAmount"
                label={t('yourBill')}
                component={AdaptSplitAmountField}
                data-testid="amount-field"
                parse={formatPrice}
                values={total || summaryTotal}
              />
              <Box>
                <Typography style={{ textAlign: 'left', marginBottom: '20px', marginTop: '20px' }}>
                  {t('selectMenuItemDesc')}
                </Typography>

                {items.length > 0 ? (
                  items.map((item) => (
                    <Item selected={select.includes(item)} item={item} setSelect={setSelect} />
                  ))
                ) : (
                  <LoadingIcon />
                )}
              </Box>
              {submitError && <InputErrorMessage>{submitError}</InputErrorMessage>}
              <Box>
                <Confirm
                  fullWidth
                  paylo
                  variant="contained"
                  type="submit"
                  loadingColor="#fff"
                  loading={submitting}
                  disabled={submitting}
                >
                  {t('confirmItems')}
                </Confirm>
              </Box>
            </Card>
          </Center>
        </form>
      )}
    />
  );
};

export default ItemList;
